import { useCallback, useMemo, useState, useEffect } from 'react';
import EmailTrackingEventHideModalAsync from 'conversations-thread-view-crm-events/email/EmailTrackingEventHideModalAsync';
import { DELETE_ENGAGEMENT, HIDE_EMAIL_TRACKING, TimelineActions } from 'crm-events-data';
import { deleteEmailTrackingActivity } from 'conversations-thread-view-crm-events/email/EmailTrackingActions';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Raven from 'raven-js';
import { getId } from 'customer-data-objects/model/ImmutableModel';
// @ts-expect-error Could not find a declaration file
import { showDeletedPausingTaskAlert } from './taskAlerts';
import TimelineTaskDependencies from 'crm-timeline-tasks/data/dependencies/TimelineTaskDependencies';
import { isTaskDependencyPausingSequence as getIsTaskDependencyPausingSequence, maybeFetchTaskDependency } from 'crm-timeline-tasks/helpers/timelineTaskUtils';
// @ts-expect-error Could not find a declaration file
import { useStoreDependency } from 'general-store';
import { EngagementDependency, SubjectDependency } from 'crm-events-data';
import CrmLogger from 'customer-data-tracking/loggers/CrmLogger';
import { getEngagementTypeForTracking } from 'crm-events-components/internal/helpers/trackingHelpers';
import TimelineEventDeleteModalAsync from 'crm-events-components/internal/engagements/delete/TimelineEventDeleteModalAsync';
import { isScheduledEmail, getDeleteTranslations } from 'crm-events-components/internal/engagements/delete/helpers/TimelineEventDeleteHelpers';
import { fromJS } from 'immutable';
import { jsx as _jsx } from "react/jsx-runtime";
export const useDeleteModalActionHandler = ({
  objectType,
  subjectId
}) => {
  const [engagementToDeleteId, setEngagementToDeleteId] = useState(null);
  const [emailTrackingTimelineItem, setEmailTrackingTimelineItem] = useState(null);
  const engagementToDelete = useStoreDependency(EngagementDependency, {
    engagementId: engagementToDeleteId
  });
  const subject = useStoreDependency(SubjectDependency, {
    objectType,
    subjectId
  });
  const taskDependency = useStoreDependency(TimelineTaskDependencies.taskDependency, {
    engagement: engagementToDelete
  });
  const sequenceEnrollment = useStoreDependency(TimelineTaskDependencies.sequenceEnrollment, {
    subjectId,
    objectType
  });
  useEffect(() => {
    maybeFetchTaskDependency(engagementToDelete, sequenceEnrollment, taskDependency);
  }, [engagementToDelete, sequenceEnrollment, taskDependency]);
  const isTaskDependencyPausingSequence = getIsTaskDependencyPausingSequence(engagementToDelete, sequenceEnrollment, taskDependency);
  const handleDeleteModalClose = useCallback(() => {
    setEngagementToDeleteId(null);
    setEmailTrackingTimelineItem(null);
  }, []);
  const handleDeleteModalAction = useCallback((actionType, data) => {
    if (actionType === DELETE_ENGAGEMENT) {
      setEngagementToDeleteId(data.engagementId);
      return;
    }
    if (actionType === HIDE_EMAIL_TRACKING) {
      const {
        timelineItem
      } = data;
      setEmailTrackingTimelineItem(fromJS(timelineItem));
      return;
    }
  }, []);
  const allDeleteTranslations = getDeleteTranslations(engagementToDelete, isTaskDependencyPausingSequence);
  const handleDeleteSuccess = useCallback(() => {
    FloatingAlertStore.addAlert({
      message: allDeleteTranslations.successfulDelete,
      type: 'success',
      'data-alert-type': 'success'
    });
    if (isTaskDependencyPausingSequence) {
      showDeletedPausingTaskAlert(taskDependency);
    }
    handleDeleteModalClose();
  }, [allDeleteTranslations.successfulDelete, handleDeleteModalClose, isTaskDependencyPausingSequence, taskDependency]);
  const handleDeleteFailed = useCallback(() => {
    return FloatingAlertStore.addAlert({
      message: allDeleteTranslations.failedDelete,
      type: 'danger',
      'data-alert-type': 'danger'
    });
  }, [allDeleteTranslations.failedDelete]);
  const handleEmailTrackingDeleteConfirm = useCallback(() => {
    const activityGuid = emailTrackingTimelineItem.getIn(['eventData', 'guid'], null);
    const trackerKey = emailTrackingTimelineItem.getIn(['eventData', 'tracker_key']);
    deleteEmailTrackingActivity({
      activityGuid,
      ownerId: emailTrackingTimelineItem.getIn(['eventData', 'owner_id']),
      subjectId: getId(subject),
      trackerKey,
      timelineItem: emailTrackingTimelineItem
    }).then(() => {
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "timeline.deleteModal.hideEmailTrackingActivity.successfulHide"
        }),
        type: 'success',
        'data-alert-type': 'success'
      });
    }).catch(() => {
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "timeline.deleteModal.hideEmailTrackingActivity.failedHide"
        }),
        type: 'danger',
        'data-alert-type': 'danger'
      });
      Raven.captureException(new Error('Failed to delete email tracking activity'), {
        extra: {
          activityGuid,
          trackerKey
        }
      });
    });
    handleDeleteModalClose();
  }, [emailTrackingTimelineItem, handleDeleteModalClose, subject]);
  const handleDeleteConfirm = useCallback(() => {
    if (emailTrackingTimelineItem) {
      handleEmailTrackingDeleteConfirm();
      return;
    }
    if (isScheduledEmail(engagementToDelete)) {
      TimelineActions.deleteScheduledEmailEngagement(engagementToDelete, handleDeleteSuccess, handleDeleteFailed);
    } else {
      TimelineActions.deleteTimelineEngagement(engagementToDelete, handleDeleteSuccess, handleDeleteFailed);
    }
    const trackingEngagementType = getEngagementTypeForTracking(engagementToDelete);
    const updatedEngagementType = trackingEngagementType === 'incoming_email' ? 'email' : trackingEngagementType;
    CrmLogger.log('timelineEngagementInteraction', {
      action: 'Delete event',
      activity: updatedEngagementType
    });
  }, [emailTrackingTimelineItem, engagementToDelete, handleEmailTrackingDeleteConfirm, handleDeleteSuccess, handleDeleteFailed]);
  const renderedDeleteModal = useMemo(() => {
    if (!engagementToDelete && !emailTrackingTimelineItem) {
      return null;
    }
    if (emailTrackingTimelineItem) {
      return /*#__PURE__*/_jsx(EmailTrackingEventHideModalAsync, {
        "data-unit-test": "email-tracking-event-modal",
        onDeleteConfirm: handleDeleteConfirm,
        onDeleteReject: handleDeleteModalClose
      });
    }
    return /*#__PURE__*/_jsx(TimelineEventDeleteModalAsync, {
      "data-unit-test": "timeline-event-delete-modal",
      engagement: engagementToDelete,
      isTaskDependencyPausingSequence: isTaskDependencyPausingSequence,
      onDeleteConfirm: handleDeleteConfirm,
      onDeleteReject: handleDeleteModalClose
    });
  }, [engagementToDelete, handleDeleteConfirm, handleDeleteModalClose, isTaskDependencyPausingSequence, emailTrackingTimelineItem]);
  return {
    handleDeleteModalAction,
    deleteModal: renderedDeleteModal
  };
};